// src/features/reservations/components/ReservationDetailContent.tsx
//
// Shared content for the reservation detail (mockup 04, §6.7 of the
// proposal): header, the six facts cards, and the guest's path timeline.
// Used both full-page (`/reservations/:id`) and inside the Llegadas de hoy
// side drawer — the drawer just wraps this in a narrower shell.
import React from 'react';
import { Box, Chip, CircularProgress, Divider, Grid, Link as MuiLink, Stack, Typography, useTheme } from '@mui/material';
import { useLocale, useTranslate } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { WidgetCard } from '@/components/common/WidgetCard';
import { EmptyState } from '@/components/common';
import { getChannelMeta } from '@/design/registry/channelRegistry';
import { getSectionMeta } from '@/design/registry/sectionRegistry';
import { hasTechnicalCapability } from '@/utils/auth';
import { StateChip } from './StateChip';
import {
  formatIdentification,
  formatKey,
  formatPayment,
  formatPaymentAmount,
  formatPreCheckIn,
  keyTypeLabel,
  paymentMethodLabel,
  relativeTime,
} from '../lib/reservationFormat';
import { formatSeconds } from '@/lib/analyticsFormat';
import { useReservationDetail } from '../lib/useReservationDetail';
import type { TimelineEntry } from '@/types/reservationState';

const formatDate = (iso: string | null, locale: string): string => {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'dd/MM/yyyy', { locale: locale === 'en' ? enUS : es });
  } catch {
    return iso;
  }
};

const formatDateTime = (iso: string, locale: string): string => {
  try {
    return format(new Date(iso), 'dd/MM HH:mm', { locale: locale === 'en' ? enUS : es });
  } catch {
    return iso;
  }
};

const timelineText = (entry: TimelineEntry, translate: ReturnType<typeof useTranslate>): string => {
  const sectionLabel = (code?: string | null) => {
    const meta = getSectionMeta(code);
    return meta ? translate(meta.labelKey) : (code ?? '');
  };
  switch (entry.kind) {
    case 'linkOpened':
      return translate('resources.reservations.detail.timeline.kinds.linkOpened');
    case 'started':
      return translate('resources.reservations.detail.timeline.kinds.started');
    case 'stopped':
      return translate('resources.reservations.detail.timeline.kinds.stopped', { section: sectionLabel(entry.section) });
    case 'resumed':
      return translate('resources.reservations.detail.timeline.kinds.resumed');
    case 'preCheckInDone':
      return translate('resources.reservations.detail.timeline.kinds.preCheckInDone', {
        identified: entry.identified ?? 0,
        total: entry.total ?? 0,
      });
    case 'checkInStarted':
      return translate('resources.reservations.detail.timeline.kinds.checkInStarted');
    case 'checkInDone':
      return translate('resources.reservations.detail.timeline.kinds.checkInDone', {
        duration: formatSeconds(entry.durationSeconds),
      });
    case 'paymentDone':
      return translate('resources.reservations.detail.timeline.kinds.paymentDone', {
        amount: formatPaymentAmount({ amount: entry.amount ?? null, currency: entry.currency ?? null }),
        method: paymentMethodLabel(entry.method, translate),
      });
    case 'paymentFailed':
      return translate('resources.reservations.detail.timeline.kinds.paymentFailed');
    case 'keyDelivered':
      return translate('resources.reservations.detail.timeline.kinds.keyDelivered', {
        keyType: keyTypeLabel(entry.keyType, translate),
        room: entry.roomNumber ?? '',
      });
    case 'keyFailed':
      return translate('resources.reservations.detail.timeline.kinds.keyFailed');
    default:
      return '';
  }
};

export interface ReservationDetailContentProps {
  reservationId: string;
  /** Show the "Ver ficha completa" link (drawer mode only). */
  linkToFull?: boolean;
}

export const ReservationDetailContent: React.FC<ReservationDetailContentProps> = ({
  reservationId,
  linkToFull = false,
}) => {
  const translate = useTranslate();
  const locale = useLocale();
  const theme = useTheme();
  const { detail, timeline, loading, error } = useReservationDetail(reservationId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !detail) {
    return <EmptyState message={translate('resources.reservations.emptyState.title')} />;
  }

  const { reservation, facts } = detail;
  const canSeeTechnical = hasTechnicalCapability();

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {reservation.guestName || reservation.reservationNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.reservations.detail.reservationNumber', { number: reservation.reservationNumber })}
            {reservation.hotelName ? ` · ${reservation.hotelName}` : ''}
            {reservation.roomNumber ? ` · ${translate('resources.reservations.detail.room', { room: reservation.roomNumber })}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.reservations.detail.arrivalDeparture', {
              arrival: formatDate(reservation.arrivalDate, locale),
              departure: formatDate(reservation.departureDate, locale),
            })}
            {reservation.adults != null ? ` · ${translate('resources.reservations.detail.adults', { count: reservation.adults })}` : ''}
            {reservation.children != null && reservation.children > 0 ? `, ${translate('resources.reservations.detail.children', { count: reservation.children })}` : ''}
            {reservation.origin ? ` · ${translate('resources.reservations.detail.origin', { origin: reservation.origin })}` : ''}
          </Typography>
        </Box>
        <StateChip state={reservation.state} />
        {linkToFull && (
          <MuiLink component={RouterLink} to={`/reservations/${reservation.id}`} sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {translate('resources.arrivals.detail.openFull')}
          </MuiLink>
        )}
      </Stack>

      {/* Six facts cards */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.preCheckIn')}>
            <Typography variant="body2">{formatPreCheckIn(facts.preCheckIn, translate, locale)}</Typography>
            {facts.preCheckIn.channel && (
              <Typography variant="caption" color="text.secondary">{translate(getChannelMeta(facts.preCheckIn.channel).labelKey)}</Typography>
            )}
          </WidgetCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.identification')}>
            <Typography variant="body2">{formatIdentification(facts.identification.identified, facts.identification.total)}</Typography>
          </WidgetCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.payment')}>
            <Typography variant="body2">{formatPayment(facts.payment, translate)}</Typography>
            {facts.payment.state === 'paid' && facts.payment.amount != null && (
              <Typography variant="caption" color="text.secondary">
                {formatPaymentAmount(facts.payment)}
              </Typography>
            )}
          </WidgetCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.checkIn')}>
            <Typography variant="body2">
              {facts.checkIn.completedAt
                ? (facts.checkIn.channel
                    ? translate(getChannelMeta(facts.checkIn.channel).labelKey)
                    : translate('resources.reservations.checkIn.reception'))
                : translate('resources.reservations.checkIn.none')}
            </Typography>
          </WidgetCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.key')}>
            <Typography variant="body2">{formatKey(facts.key, translate)}</Typography>
          </WidgetCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <WidgetCard title={translate('resources.reservations.detail.facts.stay')}>
            <Typography variant="body2">
              {facts.stay.nights != null
                ? (facts.stay.nights === 1
                    ? translate('resources.reservations.detail.facts.night')
                    : translate('resources.reservations.detail.facts.nights', { count: facts.stay.nights }))
                : '—'}
            </Typography>
          </WidgetCard>
        </Grid>
      </Grid>

      {/* Timeline */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {translate('resources.reservations.detail.timeline.title')}
      </Typography>

      {timeline && timeline.entries.length > 0 ? (
        <Stack spacing={0}>
          {timeline.attempts && (timeline.attempts.preCheckIn > 0 || timeline.attempts.kiosk > 0) && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              {timeline.attempts.preCheckIn > 0 && (
                <Chip size="small" label={translate('resources.reservations.detail.timeline.attemptsPreCheckIn', { smart_count: timeline.attempts.preCheckIn })} />
              )}
              {timeline.attempts.kiosk > 0 && (
                <Chip size="small" label={translate('resources.reservations.detail.timeline.attemptsKiosk', { smart_count: timeline.attempts.kiosk })} />
              )}
            </Stack>
          )}
          {timeline.entries.map((entry, idx) => {
            const ChannelIcon = getChannelMeta(entry.channel).Icon;
            const isFailure = entry.kind === 'paymentFailed' || entry.kind === 'keyFailed';
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: idx < timeline.entries.length - 1 ? `1px solid ${theme.palette.divider}` : 'none' }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 92, flexShrink: 0, pt: 0.25 }}>
                  {formatDateTime(entry.at, locale)}
                </Typography>
                <ChannelIcon sx={{ fontSize: 16, mt: 0.25, color: isFailure ? 'error.main' : 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: isFailure ? 'error.main' : 'text.primary' }}>
                  {timelineText(entry, translate)}
                </Typography>
              </Box>
            );
          })}
          {canSeeTechnical && timeline.technicalEventsUrl && (
            <MuiLink
              component={RouterLink}
              to={`/events?filter=${encodeURIComponent(JSON.stringify({ reservationNumber: reservation.reservationNumber }))}`}
              sx={{ mt: 2, fontSize: '0.8rem', fontWeight: 600 }}
            >
              {translate('resources.reservations.detail.timeline.technicalLink')}
            </MuiLink>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.reservations.detail.timeline.empty')}
        </Typography>
      )}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default ReservationDetailContent;
